/**
 * Renders lightweight content markup used by DB-driven public pages.
 * Conventions (one block per line, blank line separates blocks):
 *   '## '  -> section heading (h2)
 *   '### ' -> sub-heading (h3)
 *   '!! '  -> emphasized all-caps legal provision
 *   '- '   -> list item (consecutive items become one <ul>)
 *   anything else (non-empty) -> paragraph
 * Plain multi-paragraph text (blank-line separated) renders as clean paragraphs.
 */
export function MarkupText({ text, className = '' }) {
  if (!text) return null;
  const lines = String(text).replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let list = null;

  const flushList = () => {
    if (list && list.length) {
      blocks.push({ type: 'ul', items: list });
    }
    list = null;
  };

  lines.forEach((raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) { flushList(); return; }
    if (line.startsWith('- ')) {
      if (!list) list = [];
      list.push(line.slice(2));
      return;
    }
    flushList();
    if (line.startsWith('### ')) blocks.push({ type: 'h3', text: line.slice(4) });
    else if (line.startsWith('## ')) blocks.push({ type: 'h2', text: line.slice(3) });
    else if (line.startsWith('!! ')) blocks.push({ type: 'caps', text: line.slice(3) });
    else blocks.push({ type: 'p', text: line });
  });
  flushList();

  return (
    <div className={className} data-testid="markup-text">
      {blocks.map((b, i) => {
        if (b.type === 'h2') return <h2 key={i} className="text-xl sm:text-2xl font-bold text-slate-900 mt-10 mb-3">{b.text}</h2>;
        if (b.type === 'h3') return <h3 key={i} className="text-base sm:text-lg font-semibold text-slate-800 mt-6 mb-2">{b.text}</h3>;
        if (b.type === 'caps') return <p key={i} className="text-[13px] sm:text-sm font-semibold tracking-wide text-slate-800 my-3 leading-relaxed">{b.text}</p>;
        if (b.type === 'ul') return (
          <ul key={i} className="list-disc pl-6 my-3 space-y-1.5 text-[15px] text-slate-700 leading-relaxed">
            {b.items.map((li, j) => <li key={j}>{li}</li>)}
          </ul>
        );
        return <p key={i} className="text-[15px] text-slate-700 leading-relaxed my-3">{b.text}</p>;
      })}
    </div>
  );
}

export default MarkupText;
